import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Box, Info } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store';
import { fetchEmiDetails, clearSelectedEmiDetails } from '../../../store/slices/acfSlice';
import { formatDateToDdmmyyyy, formatRawDateTime } from '../../../utils/format';

const ACFUserDetails: React.FC = () => {
    const { userId, orderId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { selectedEmiDetails, emiLoading, acfOrders } = useAppSelector((state: RootState) => state.acf);
    const adminMobile = useAppSelector((state: RootState) => state.auth.adminMobile || '9999999999');

    // Find order details from the list in store if available
    const orderInfo = useMemo(() => {
        return acfOrders.find(o => o.id === orderId || o.orderId === orderId);
    }, [acfOrders, orderId]);

    useEffect(() => {
        if (orderId && userId) {
            dispatch(fetchEmiDetails({ orderId, userId, adminMobile }));
        }
        return () => {
            dispatch(clearSelectedEmiDetails());
        };
    }, [dispatch, orderId, userId, adminMobile]);

    // Data derivation from schedule
    const stats = useMemo(() => {
        if (!selectedEmiDetails || !Array.isArray(selectedEmiDetails)) return { paid: 0, pending: 0, total: 0, count: 0, paidCount: 0 };

        const paid = selectedEmiDetails
            .filter(e => e.status === 'PAID')
            .reduce((acc, curr) => acc + (curr.amount || 0), 0);

        const total = selectedEmiDetails.reduce((acc, curr) => acc + (curr.amount || 0), 0);

        return {
            paid,
            total,
            pending: total - paid,
            count: selectedEmiDetails.length,
            paidCount: selectedEmiDetails.filter(e => e.status === 'PAID').length
        };
    }, [selectedEmiDetails]);

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'PAID': return 'Paid';
            case 'PENDING': return 'Pending Payment';
            case 'WAITING_FOR_APPROVAL': return 'Admin Approval pending';
            case 'REJECTED': return 'Rejected';
            default: return status?.replace(/_/g, ' ') || 'UNKNOWN';
        }
    };

    const userDisplay = {
        name: orderInfo?.user?.name || 'User Details',
        mobile: orderInfo?.user?.mobile || '-',
        joinDate: orderInfo?.createdAt ? formatDateToDdmmyyyy(orderInfo.createdAt) : 'N/A',
        units: orderInfo?.units || '-'
    };

    if (emiLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin"></div>
                <p className="text-slate-500 font-medium">Loading installment details...</p>
            </div>
        );
    }

    return (
        <div className="w-full pb-12 font-sans">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">ACF Details</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 font-medium transition-colors">ACF</button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">ACF Details</span>
                </nav>
            </div>

            <div className="space-y-6">
                {/* User Information */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">User Information</h2>
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {/* Card Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                            <div className="p-2 bg-white border border-slate-200 rounded-lg shadow-sm text-slate-600">
                                <User size={18} />
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-base font-bold text-slate-800">{userDisplay.name}</span>
                                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold border border-blue-100 uppercase tracking-wider">ACF Plan</span>
                            </div>
                        </div>
                        {/* Info Grid */}
                        <div className="px-6 py-5 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                            <InfoItem label="Mobile" value={userDisplay.mobile ? `+91 ${userDisplay.mobile}` : '-'} />
                            <InfoItem label="Order ID" value={orderId} />
                            <InfoItem label="Units Purchased" value={`${userDisplay.units} ${Number(userDisplay.units) === 1 ? 'Unit' : 'Units'}`} />
                        </div>
                    </div>
                </div>

                {/* Plan Summary */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">Plan Summary</h2>
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        {/* Card Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                            <div className="p-2 bg-white border border-slate-200 rounded-lg shadow-sm text-slate-600">
                                <Box size={18} />
                            </div>
                            <span className="text-base font-bold text-slate-800">Plan Summary</span>
                        </div>
                        {/* Info Grid */}
                        <div className="px-6 py-5 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-5">
                            <InfoItem label="Total Plan Value" value={`₹${stats.total.toLocaleString('en-IN')} (${stats.count} Installments)`} />
                            <InfoItem label="Amount Paid" value={`₹${stats.paid.toLocaleString('en-IN')} (${Math.round((stats.paid / (stats.total || 1)) * 100)}%)`} />
                            <div className="flex flex-col min-w-0">
                                <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1 whitespace-nowrap">Remaining Amount</span>
                                <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">
                                    {stats.pending > 0 ? `₹${stats.pending.toLocaleString('en-IN')}` : '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Installment Schedule */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">Installment Schedule</h2>
                    {!selectedEmiDetails || selectedEmiDetails.length === 0 ? (
                        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                                <Info size={40} strokeWidth={1.5} />
                                <p className="text-sm font-medium">No installment records found for this order</p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                            <table className="w-full border-collapse">
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-slate-50 border-b-2 border-slate-200">
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">EMI No</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Due Date</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Paid Date</th>
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {selectedEmiDetails.map((emi: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-4 py-3 text-sm text-slate-700 font-bold whitespace-nowrap">
                                                #{emi.installmentNumber || idx + 1}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                {emi.dueDate ? formatDateToDdmmyyyy(emi.dueDate) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[10px] font-bold border tracking-wide shadow-sm whitespace-nowrap
                                                    ${emi.status === 'PAID' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                        emi.status === 'WAITING_FOR_APPROVAL' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                            emi.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                                'bg-slate-50 text-slate-700 border-slate-200'
                                                    }`}>
                                                    {getStatusLabel(emi.status)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">
                                                {emi.paidAt ? formatRawDateTime(emi.paidAt) : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700 text-right font-bold whitespace-nowrap">
                                                ₹{(emi.amount || 0).toLocaleString('en-IN')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const InfoItem = ({ label, value }: { label: string; value?: string | number | null }) => (
    <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1 whitespace-nowrap">{label}</span>
        <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{value || '-'}</span>
    </div>
);

export default ACFUserDetails;
